import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useMemo, useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { JobInvoicing } from '~hooks/useJob/models';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import {
  BILLING_LINE_SIDES,
  getBillingLinesParties,
  getLinesOfSide,
} from '../chargeSelection';
import { JobCharges } from '../groupBillingLines';
import ChargesInvoiceEditDialog from './ChargesInvoiceEditDialog';

const sideLabels = {
  [BillingLineSide.PAYABLE]: {
    noInvoice: () => t('approvals.approve_charges.add_on_actions.no_invoice.payable'),
    side: () => t('approvals.approve_charges.selection.side.payable'),
    sideWithParty: (party: string) =>
      t('approvals.approve_charges.add_on_actions.side_with_party.payable', { party }),
  },
  [BillingLineSide.RECEIVABLE]: {
    noInvoice: () => t('approvals.approve_charges.add_on_actions.no_invoice.receivable'),
    side: () => t('approvals.approve_charges.selection.side.receivable'),
    sideWithParty: (party: string) =>
      t('approvals.approve_charges.add_on_actions.side_with_party.receivable', { party }),
  },
} satisfies Record<
  BillingLineSide,
  {
    noInvoice: () => string;
    side: () => string;
    sideWithParty: (party: string) => string;
  }
>;

interface SideOption {
  hasInvoice: boolean;
  partyName: string | null;
  side: BillingLineSide;
}

interface SideMenuButtonProps {
  children: React.ReactNode;
  icon?: React.ReactElement;
  onPickSide: (side: BillingLineSide) => void;
  sideOptions: SideOption[];
  variant: 'outlined' | 'text';
}

function SideMenuButton({
  children,
  icon,
  onPickSide,
  sideOptions,
  variant,
}: SideMenuButtonProps) {
  const menuHandler = useRef<MenuHandler>(null);

  return (
    <Menu
      ref={menuHandler}
      menuTrigger={
        <SmallButton color="secondary" startIcon={icon} variant={variant}>
          {children}
        </SmallButton>
      }
    >
      {sideOptions.map(({ hasInvoice, partyName, side }) => (
        <MenuItem
          key={side}
          disabled={!hasInvoice}
          onClick={(event) => {
            event.stopPropagation();
            menuHandler.current?.onClose?.();
            onPickSide(side);
          }}
        >
          <ListItemText
            primary={
              partyName
                ? sideLabels[side].sideWithParty(partyName)
                : sideLabels[side].side()
            }
            secondary={hasInvoice ? undefined : sideLabels[side].noInvoice()}
          />
        </MenuItem>
      ))}
    </Menu>
  );
}

interface ChargesAddOnActionsProps {
  charges?: JobCharges;
  job: JobInvoicing;
  sides?: BillingLineSide[];
}

export default function ChargesAddOnActions({
  charges,
  job,
  sides = BILLING_LINE_SIDES,
}: ChargesAddOnActionsProps) {
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const [editedSide, setEditedSide] = useState<BillingLineSide | null>(null);
  const hasAddOns = Boolean(charges && charges.addOns.length > 0);

  const sideOptions = useMemo(
    () =>
      sides.map((side) => {
        const parties = getBillingLinesParties(
          getLinesOfSide(charges?.lines ?? [], side),
        );
        const invoice =
          side === BillingLineSide.PAYABLE ? job.payableInvoice : job.receivableInvoice;

        return {
          hasInvoice: Boolean(invoice),
          partyName: parties.length === 1 ? parties[0].name : null,
          side,
        };
      }),
    [charges?.lines, job.payableInvoice, job.receivableInvoice, sides],
  );

  const handleClose = () => {
    setEditedSide(null);
    reload();
  };

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      <SideMenuButton
        icon={<Add />}
        onPickSide={setEditedSide}
        sideOptions={sideOptions}
        variant="outlined"
      >
        {t('approvals.approve_charges.add_on_actions.add')}
      </SideMenuButton>
      {hasAddOns && (
        <SideMenuButton
          onPickSide={setEditedSide}
          sideOptions={sideOptions}
          variant="text"
        >
          {t('approvals.approve_charges.add_on_actions.edit')}
        </SideMenuButton>
      )}

      {editedSide && (
        <ChargesInvoiceEditDialog
          jobId={job.id}
          onClose={handleClose}
          side={editedSide}
        />
      )}
    </Box>
  );
}
